<script lang="ts" setup>
import moment from 'moment';
import orderBy from 'lodash/orderBy';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import { applyPhoneMask } from '@/utils/number';
import { formatSecToTime } from '@/utils/datetime';
import {
    UserDeviceStatus,
    UserQueueStatus,
    UserExtensionQuality,
    RealtimeReportServiceGetUserDevicesResult,
} from '@/types';

const { t } = useI18n();

const props = defineProps<{
    userDevices: RealtimeReportServiceGetUserDevicesResult[];
}>();

const pingToQuality = (ping: number | null | undefined): UserExtensionQuality => {
    if (typeof ping !== 'number' || ping < 1) return UserExtensionQuality.UNAVAILABLE;
    if (ping > 205) return UserExtensionQuality.LOW;
    if (ping > 150) return UserExtensionQuality.MEDIUM;
    return UserExtensionQuality.HIGH;
};

const getCallDuration = (callCreationTime: Date | null | undefined): string => {
    if (!callCreationTime) return '';
    const seconds = moment.duration(moment().diff(moment(callCreationTime))).asSeconds();
    return formatSecToTime(seconds);
};
const computedUserDevices = computed(() => {
    const sorted = orderBy(
        props.userDevices,
        [
            (ud) => ud.user.extension?.number || '',
            (ud) => ud.user.name || '',
            (ud) => ud.deviceType || '',
        ],
        ['asc', 'asc', 'asc'],
    );

    return sorted.map((ud) => ({
        key: `${ud.user.id}_${ud.deviceType}`,
        extension: ud.user.extension?.number || '',
        name: ud.user.name || '',
        deviceTypeLabel: t(`core.device.${ud.deviceType}`),
        statusLabel: t(
            `report.realtime.device.status.${ud.state?.status || UserDeviceStatus.OFFLINE}`,
        ),
        statusClass:
            !ud.state?.status || ud.state.status === UserDeviceStatus.OFFLINE
                ? 'bg-danger-100 text-danger-700'
                : 'bg-success-50 text-success-700',
        queueStatusLabel: t(
            `report.realtime.user.queueStatus.${ud.user?.queueStatusHistory?.length ? UserQueueStatus.PAUSED : UserQueueStatus.UNPAUSED}`,
        ),
        queueStatusClass: ud.user?.queueStatusHistory?.length
            ? 'bg-danger-100 text-danger-700'
            : 'bg-success-50 text-success-700',
        phoneNumber: ud.state?.phoneNumber ? applyPhoneMask(ud.state.phoneNumber) : '',
        qualityLabel: t(`report.realtime.device.quality.${pingToQuality(ud.state?.ping)}`),
        callTypeLabel: ud.state?.callType ? t(`call.type.${ud.state.callType}`) : '',
        duration: getCallDuration(ud.state?.callCreationTime),
    }));
});
</script>

<template>
    <div class="relative flex flex-col">
        <h1 class="mb-2 text-lg font-semibold">
            {{ $t('report.realtime.userDevicesTable.title') }}
        </h1>
        <UiPanel class="flex-1 overflow-y-auto" no-padding>
            <table class="min-w-full border-separate border-spacing-0">
                <thead>
                    <tr>
                        <th
                            v-for="header in [
                                'extension',
                                'user',
                                'device',
                                'status',
                                'queueStatus',
                                'numberClient',
                                'quality',
                                'type',
                                'duration',
                            ]"
                            :key="header"
                            class="sticky top-0 z-10 whitespace-nowrap border-b border-gray-300 bg-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-800 backdrop-blur backdrop-filter"
                            :class="{
                                'text-left': [
                                    'extension',
                                    'user',
                                    'numberClient',
                                    'quality',
                                ].includes(header),
                                'text-center': [
                                    'device',
                                    'status',
                                    'queueStatus',
                                    'quality',
                                    'type',
                                    'duration',
                                ].includes(header),
                            }"
                        >
                            {{ $t(`report.realtime.userDevicesTable.header.${header}`) }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="item in computedUserDevices" :key="item.id" class="even:bg-gray-100">
                        <td
                            class="whitespace-nowrap border-b border-gray-200 px-3 py-1.5 text-sm font-medium"
                        >
                            {{ item.extension }}
                        </td>
                        <td
                            class="whitespace-nowrap border-b border-gray-200 px-3 py-1.5 text-sm font-medium"
                        >
                            {{ item.name }}
                        </td>
                        <td
                            class="whitespace-nowrap border-b border-gray-200 px-3 py-1.5 text-center text-sm font-medium"
                        >
                            {{ item.deviceTypeLabel }}
                        </td>
                        <td
                            :class="[
                                item.statusClass,
                                'whitespace-nowrap border-b border-gray-200 px-3 py-1.5 text-center text-sm font-medium',
                            ]"
                        >
                            {{ item.statusLabel }}
                        </td>
                        <td
                            :class="[
                                item.queueStatusClass,
                                'whitespace-nowrap border-b border-gray-200 px-3 py-1.5 text-center text-sm font-medium',
                            ]"
                        >
                            {{ item.queueStatusLabel }}
                        </td>
                        <td
                            class="whitespace-nowrap border-b border-gray-200 px-3 py-1.5 text-sm font-medium"
                        >
                            {{ item.phoneNumber }}
                        </td>
                        <td
                            class="whitespace-nowrap border-b border-gray-200 px-3 py-1.5 text-center text-sm font-medium"
                        >
                            {{ item.qualityLabel }}
                        </td>
                        <td
                            class="whitespace-nowrap border-b border-gray-200 px-3 py-1.5 text-center text-sm font-medium"
                        >
                            {{ item.callTypeLabel }}
                        </td>
                        <td
                            class="whitespace-nowrap border-b border-gray-200 px-3 py-1.5 text-center text-sm font-medium"
                        >
                            {{ item.duration }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </UiPanel>
    </div>
</template>
